import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from "@/utils/languageRouter";
import axios from "axios";
import { Calendar, Eye, ArrowRight, BookOpen, TrendingUp, Clock, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SEOHead from "@/components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPage = () => {
  const { t, i18n } = useTranslation();
  const { lang: urlLang } = useParams();
  const lang = urlLang || i18n.language;
  const currentLang = SUPPORTED_LANGUAGES.includes(lang) ? lang : DEFAULT_LANGUAGE;
  
  const localizedLink = (path) => {
    const cleanPath = path.startsWith("/") ? path : `/${path}`;
    return `/${currentLang}${cleanPath}`;
  };
  
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  // Check if user is admin
  const isAdmin = () => {
    const token = localStorage.getItem("token");
    if (!token) return false;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.role === "admin";
    } catch {
      return false;
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchPosts();
  }, [filter]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/blog/categories/list`);
      setCategories(res.data);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = filter !== "all" ? { category: filter } : {};
      const res = await axios.get(`${API}/blog`, { params });
      setPosts(res.data);
    } catch (err) {
      console.error("Error fetching posts:", err);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (catId) => {
    const cat = categories.find((c) => c.id === catId);
    return cat?.name?.[lang] || cat?.name?.fr || catId;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getText = (obj) => {
    if (!obj) return "";
    if (typeof obj === "string") return obj;
    return obj[lang] || obj.fr || obj.en || "";
  };

  // Separate featured (first 2) and rest
  const featuredPosts = posts.slice(0, 2);
  const regularPosts = posts.slice(2);
  
  // Get most viewed posts (sort by views)
  const popularPosts = [...posts].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 4);

  return (
    <div className="min-h-screen bg-background" data-testid="blog-page">
      <SEOHead 
        customTitle={lang === "fr" ? "Blog Voyage Désert - Conseils & Inspirations | XploreSahara" : "Desert Travel Blog - Tips & Inspirations | XploreSahara"}
        customDescription={lang === "fr" ? "Découvrez nos articles sur le Sahara marocain : conseils de voyage, culture berbère, destinations, photographie et bien plus." : "Discover our articles about the Moroccan Sahara: travel tips, Berber culture, destinations, photography and more."}
      />

      {/* Hero Header */}
      <div className="relative bg-gradient-to-b from-primary/20 to-background pt-24 pb-16">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1509023464722-18d996393ca8?w=1920')] bg-cover bg-center opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center">
            <Badge variant="outline" className="mb-4 border-primary text-primary">
              <BookOpen className="h-3 w-3 mr-1" />
              {lang === "fr" ? "Blog Voyage Désert" : "Desert Travel Blog"}
            </Badge>
            <h1 className="font-serif text-4xl md:text-6xl font-bold mt-2 mb-6">
              {lang === "fr" ? "Inspirations & Conseils" : "Inspirations & Tips"}
            </h1>
            <p className="text-muted-foreground text-lg max-w-3xl mx-auto leading-relaxed">
              {lang === "fr" 
                ? "Plongez dans l'univers fascinant du Sahara marocain. Découvrez nos articles sur la culture berbère, les destinations incontournables et nos conseils pour préparer votre aventure."
                : "Dive into the fascinating world of the Moroccan Sahara. Discover our articles on Berber culture, must-see destinations and our tips for preparing your adventure."
              }
            </p>
          </div>
        </div>
      </div>

      {/* Category Filters */}
      <div className="sticky top-16 z-40 bg-background/95 backdrop-blur-md border-b">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex flex-wrap gap-2 justify-center">
            <Button
              variant={filter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("all")}
              className="rounded-full"
            >
              {lang === "fr" ? "Tous les articles" : "All articles"}
            </Button>
            {categories.map((cat) => (
              <Button
                key={cat.id}
                variant={filter === cat.id ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter(cat.id)}
                className="rounded-full"
              >
                {cat.name?.[lang] || cat.name?.fr}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="aspect-[16/10] bg-muted rounded-2xl animate-pulse" />
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-[4/5] bg-muted rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* Featured Posts - Large Cards */}
          {featuredPosts.length > 0 && filter === "all" && (
            <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
              <div className="flex items-center gap-2 mb-8">
                <TrendingUp className="h-5 w-5 text-primary" />
                <h2 className="text-2xl font-serif font-bold">
                  {lang === "fr" ? "À la Une" : "Featured"}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {featuredPosts.map((post) => (
                  <Link 
                    key={post.id} 
                    to={localizedLink(`/blog/${post.slug || post.id}`)}
                    className="group relative overflow-hidden rounded-2xl bg-muted aspect-[16/10]"
                  >
                    <img
                      src={post.image_url || post.cover_image || "https://images.unsplash.com/photo-1509023464722-18d996393ca8?w=800"}
                      alt={getText(post.title)}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                      <Badge className="mb-3 bg-primary/90">
                        {getCategoryName(post.category)}
                      </Badge>
                      <h3 className="text-white font-serif text-2xl md:text-3xl font-bold mb-3 group-hover:text-primary transition-colors">
                        {getText(post.title)}
                      </h3>
                      <p className="text-white/80 line-clamp-2 mb-4 hidden md:block">
                        {getText(post.summary) || getText(post.excerpt)}
                      </p>
                      <div className="flex items-center gap-4 text-white/60 text-sm">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDate(post.created_at)}
                        </span>
                        {post.author && (
                          <span className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {post.author}
                          </span>
                        )}
                        {isAdmin() && (
                          <span className="flex items-center gap-1">
                            <Eye className="h-4 w-4" />
                            {post.views || 0}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Main Content Grid */}
          <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              {/* Articles Grid */}
              <div className="lg:col-span-3">
                <div className="flex items-center gap-2 mb-8">
                  <BookOpen className="h-5 w-5 text-primary" />
                  <h2 className="text-2xl font-serif font-bold">
                    {filter === "all" 
                      ? (lang === "fr" ? "Tous les Articles" : "All Articles")
                      : getCategoryName(filter)
                    }
                  </h2>
                  <span className="text-muted-foreground">({posts.length})</span>
                </div>

                {(filter === "all" ? regularPosts : posts).length === 0 ? (
                  <div className="text-center py-16 bg-muted/30 rounded-2xl">
                    <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      {lang === "fr" ? "Aucun article dans cette catégorie" : "No articles in this category"}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {(filter === "all" ? regularPosts : posts).map((post) => (
                      <Link
                        key={post.id}
                        to={localizedLink(`/blog/${post.slug || post.id}`)}
                        className="group bg-background rounded-xl overflow-hidden border hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                      >
                        <div className="relative aspect-[4/3] overflow-hidden">
                          <img
                            src={post.image_url || post.cover_image || "https://images.unsplash.com/photo-1509023464722-18d996393ca8?w=600"}
                            alt={getText(post.title)}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          />
                          <div className="absolute top-3 left-3">
                            <Badge variant="secondary" className="bg-white/90 text-foreground">
                              {getCategoryName(post.category)}
                            </Badge>
                          </div>
                        </div>
                        <div className="p-5">
                          <h3 className="font-serif text-lg font-semibold mb-2 line-clamp-2 group-hover:text-primary transition-colors">
                            {getText(post.title)}
                          </h3>
                          <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                            {getText(post.summary) || getText(post.excerpt)}
                          </p>
                          <div className="flex items-center justify-between text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {formatDate(post.created_at)}
                            </span>
                            {isAdmin() && (
                              <span className="flex items-center gap-1">
                                <Eye className="h-3 w-3" />
                                {post.views || 0} vues
                              </span>
                            )}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <aside className="space-y-8">
                {/* Popular Posts */}
                <div className="bg-muted/30 rounded-2xl p-6">
                  <h3 className="font-serif text-lg font-bold mb-4 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    {lang === "fr" ? "Articles Populaires" : "Popular Articles"}
                  </h3>
                  <div className="space-y-4">
                    {popularPosts.map((post, index) => (
                      <Link
                        key={post.id}
                        to={localizedLink(`/blog/${post.slug || post.id}`)}
                        className="flex gap-3 group"
                      >
                        <span className="text-3xl font-bold text-primary/30 group-hover:text-primary transition-colors">
                          {String(index + 1).padStart(2, '0')}
                        </span>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm line-clamp-2 group-hover:text-primary transition-colors">
                            {getText(post.title)}
                          </h4>
                          {isAdmin() && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {post.views || 0} vues
                            </p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Categories */}
                <div className="bg-muted/30 rounded-2xl p-6">
                  <h3 className="font-serif text-lg font-bold mb-4">
                    {lang === "fr" ? "Catégories" : "Categories"}
                  </h3>
                  <div className="space-y-2">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        onClick={() => setFilter(cat.id)}
                        className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                          filter === cat.id 
                            ? "bg-primary text-white" 
                            : "hover:bg-muted"
                        }`}
                      >
                        {cat.name?.[lang] || cat.name?.fr}
                      </button>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <div className="bg-gradient-to-br from-primary to-primary/80 rounded-2xl p-6 text-white">
                  <h3 className="font-serif text-lg font-bold mb-2">
                    {lang === "fr" ? "Prêt pour l'aventure ?" : "Ready for adventure?"}
                  </h3>
                  <p className="text-white/80 text-sm mb-4">
                    {lang === "fr" 
                      ? "Découvrez nos circuits dans le Sahara marocain"
                      : "Discover our tours in the Moroccan Sahara"
                    }
                  </p>
                  <Button asChild variant="secondary" className="w-full">
                    <Link to={localizedLink("/circuits/4x4")}>
                      {lang === "fr" ? "Voir les circuits" : "View tours"}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </aside>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default BlogPage;
